import { BENCHMARK_TYPES, SECTION_META } from "../../constants/benchmarkTypes";

/**
 * Benchmark comparisons built only from each KPI's own LB/UB/MB bounds and Type (1-5).
 * Reference points are what a company reporting the reference value on every KPI would
 * score, using the same normalisation as the SI rating — no synthetic country, region,
 * size or cohort adjustments. Keep in sync with SI/Backend/src/util/benchmarkingEngine.js.
 */

const n = (v) => {
  const parsed = Number(v);
  return v === null || v === "" || !Number.isFinite(parsed) ? null : parsed;
};

const clamp01 = (x) => Math.max(0, Math.min(1, x));

/** Coerce API refs / DTOs / strings into a safe display label. */
export function asDisplayLabel(value, fallback = "") {
  if (value == null || value === "") return fallback;
  if (typeof value === "string" || typeof value === "number") {
    const text = String(value).trim();
    return text && text !== "[object Object]" ? text : fallback;
  }
  if (typeof value === "object") {
    const nested =
      value.name ??
      value.label ??
      value.title ??
      value.range ??
      value.country_name ??
      value.sector_name ??
      value.value;
    if (nested != null && nested !== value) {
      return asDisplayLabel(nested, fallback);
    }
  }
  return fallback;
}

/** Same bands and strict edges as the backend getGrade(). */
export function gradeFromScore(score) {
  if (score == null || !Number.isFinite(score)) return "—";
  if (score > 75) return "AA";
  if (score > 70) return "AB";
  if (score > 65) return "BB";
  if (score > 60) return "BC";
  if (score > 55) return "CC";
  if (score > 50) return "CD";
  if (score > 40) return "DD";
  return "FF";
}

export function extractOrgContext(report = {}) {
  const org =
    report.organizationDetails?.organization ||
    report.organizationDetails?.organizationDetails ||
    {};
  const country = asDisplayLabel(org.country ?? report.country, "Global");
  const industry = asDisplayLabel(org.industry ?? report.sector, "");
  const sector = asDisplayLabel(
    report.formSchemaSector ?? report.sector ?? industry,
    industry,
  );
  return { country, industry, sector };
}

export function extractSectionScores(report = {}) {
  return {
    base: n(report.basicSectorSpecificKPI?.score) ?? 0,
    environment: n(report.environmentKPI?.score) ?? 0,
    social: n(report.socialKPI?.score) ?? 0,
    governance: n(report.governanceKPI?.score) ?? 0,
    overall: n(report.score) ?? 0,
  };
}

function normalizeSection(section) {
  const s = String(section || "").toLowerCase();
  if (s.includes("environment")) return "environment";
  if (s.includes("social")) return "social";
  if (s.includes("governance") || s.includes("economic")) return "governance";
  return "base";
}

function kpiType(schema) {
  const t = n(schema?.type);
  if (t) return t;
  const unit = String(schema?.unit || "").toLowerCase();
  if (unit === "0/1" || unit === "yes/no") return 3;
  return inferLowerIsBetter(schema) ? 2 : 1;
}

function inferLowerIsBetter(schema) {
  const formula = String(schema?.formula || "").toLowerCase();
  const unit = String(schema?.unit || "").toLowerCase();
  if (unit.includes("%") && /(recycl|renew|women|female|train|resolv)/.test(`${formula} ${schema?.kpi || ""}`.toLowerCase())) {
    return false;
  }
  return unit.includes("/") || formula.includes("/");
}

function bounds(schema) {
  const { LB, UB, MB } = schema?.bounds || {};
  const lb = n(LB);
  const ub = n(UB);
  const mb = n(MB) ?? (lb != null && ub != null ? (lb + ub) / 2 : null);
  return { lb, ub, mb };
}

export function normalizeKpiValue(value, schema) {
  const v = n(value);
  if (v == null) return null;
  const type = kpiType(schema);
  const { lb, ub, mb } = bounds(schema);
  if (type === 3) return v ? 1 : 0;
  if (type === 4) return v === 0 ? 1 : 0;
  if (lb == null || ub == null) return null;
  if (type === 1) return ub === lb ? 1 : clamp01((v - lb) / (ub - lb));
  if (type === 2) return ub === lb ? 1 : clamp01((ub - v) / (ub - lb));
  if (type === 5) {
    if (v < mb) return mb === lb ? 1 : clamp01((v - lb) / (mb - lb));
    return mb === ub ? 1 : clamp01((v - ub) / (mb - ub));
  }
  return null;
}

/** Best reportable value (normalises to 1). */
function bestValue(schema) {
  const type = kpiType(schema);
  const { lb, ub, mb } = bounds(schema);
  if (type === 3) return 1;
  if (type === 4) return 0;
  if (type === 1) return ub;
  if (type === 2) return lb;
  if (type === 5) return mb;
  return null;
}

/** Midpoint of the peer / researched range. */
function typicalValue(schema) {
  const { lb, ub, mb } = bounds(schema);
  if (kpiType(schema) === 5) return mb;
  return lb != null && ub != null ? (lb + ub) / 2 : null;
}

function benchmarkKpiValue(schema, benchmarkType, overrides) {
  if (benchmarkType === "previous_assessment" || benchmarkType === "custom") {
    const v = overrides?.[schema.code];
    return v != null ? v : typicalValue(schema);
  }
  if (benchmarkType === "industry_leaders" || benchmarkType === "global_best_practices") {
    return bestValue(schema);
  }
  return typicalValue(schema);
}

/** Section scores (0-100) of a company reporting the benchmark value on every KPI. */
function referenceSectionScores(schemas, benchmarkType, overrides) {
  const totals = { base: 0, environment: 0, social: 0, governance: 0 };
  const weightSum = { base: 0, environment: 0, social: 0, governance: 0 };
  const counted = { base: 0, environment: 0, social: 0, governance: 0 };
  schemas.forEach((schema) => {
    const key = normalizeSection(schema.section);
    const w = n(schema.weight) || 0;
    weightSum[key] += w;
    const norm = normalizeKpiValue(benchmarkKpiValue(schema, benchmarkType, overrides), schema);
    if (norm == null) return;
    totals[key] += w * norm;
    counted[key] += 1;
  });
  const sections = {};
  Object.keys(totals).forEach((k) => {
    // Section weights are stored either as fractions (sum ≈ 1) or as points (sum ≈ 100).
    const toPoints = weightSum[k] > 1.5 ? 1 : 100;
    sections[k] = counted[k] ? totals[k] * toPoints : null;
  });
  const present = Object.values(sections).filter((v) => v != null);
  sections.overall = present.length ? present.reduce((a, b) => a + b, 0) / present.length : null;
  return sections;
}

function referenceForType(benchmarkType, { schemas, priorScores, customTargets, priorByCode }) {
  if (benchmarkType === "previous_assessment") return priorScores || null;
  if (benchmarkType === "custom" && customTargets) return customTargets;
  return referenceSectionScores(schemas, benchmarkType, priorByCode);
}

export function buildKpiComparisons({
  schemas = [],
  submissions = [],
  benchmarkType,
  priorSubmissions = [],
  customKpiTargets = {},
}) {
  const latestBySchema = {};
  submissions.forEach((sub) => {
    const schemaId = String(sub.schema?._id || sub.schema?.id || sub.schema || "");
    if (!latestBySchema[schemaId] || new Date(sub.createdAt) > new Date(latestBySchema[schemaId].createdAt)) {
      latestBySchema[schemaId] = sub;
    }
  });

  const priorByCode = {};
  priorSubmissions.forEach((sub) => {
    const code = sub.schema?.code || sub.schemaCode;
    const val = sub.calculated?.kpiValue ?? sub.values?.kpiValue;
    if (code && val != null) priorByCode[code] = val;
  });

  return schemas
    .map((schema) => {
      const schemaId = String(schema._id || schema.id);
      const submission = latestBySchema[schemaId];
      const orgValue = n(submission?.calculated?.kpiValue);
      const benchmarkValue = benchmarkKpiValue(
        schema,
        benchmarkType,
        benchmarkType === "custom" ? customKpiTargets : priorByCode,
      );
      if (orgValue == null && benchmarkValue == null) return null;

      const type = kpiType(schema);
      const lowerIsBetter = type === 2 || type === 4;
      const orgNormalized = normalizeKpiValue(orgValue, schema);
      const benchmarkNormalized = normalizeKpiValue(benchmarkValue, schema);

      let gap = null;
      let gapPercent = null;
      let status = "no_data";
      if (orgValue != null && benchmarkValue != null) {
        gap = orgValue - benchmarkValue;
        gapPercent = benchmarkValue !== 0 ? (gap / Math.abs(benchmarkValue)) * 100 : null;
        if (orgNormalized != null && benchmarkNormalized != null) {
          const d = orgNormalized - benchmarkNormalized;
          status = d > 0.05 ? "ahead" : d < -0.05 ? "behind" : "on_par";
        }
      }

      return {
        code: schema.code,
        name: schema.kpi || schema.name,
        section: normalizeSection(schema.section),
        unit: schema.unit || "",
        type,
        orgValue,
        benchmarkValue,
        orgNormalized,
        benchmarkNormalized,
        gap,
        gapPercent,
        status,
        lowerIsBetter,
        weight: schema.weight || 0,
      };
    })
    .filter(Boolean)
    .sort((a, b) => Math.abs(b.gapPercent || 0) - Math.abs(a.gapPercent || 0));
}

/** Section scores (0-100) from KPI normalizations (FormSubmissions). */
export function orgSectionScoresFromKpis(kpis = []) {
  const totals = { base: 0, environment: 0, social: 0, governance: 0 };
  const weightSum = { base: 0, environment: 0, social: 0, governance: 0 };
  const counted = { base: 0, environment: 0, social: 0, governance: 0 };
  kpis.forEach((kpi) => {
    if (kpi?.orgNormalized == null) return;
    const key = normalizeSection(kpi.section);
    const w = n(kpi.weight) || 0;
    weightSum[key] += w;
    totals[key] += w * kpi.orgNormalized;
    counted[key] += 1;
  });
  const sections = {};
  Object.keys(totals).forEach((k) => {
    const toPoints = weightSum[k] > 1.5 ? 1 : 100;
    sections[k] = counted[k] ? totals[k] * toPoints : null;
  });
  const present = Object.values(sections).filter((v) => v != null);
  sections.overall = present.length
    ? present.reduce((a, b) => a + b, 0) / present.length
    : null;
  return sections;
}

function resolveOrgScores(report, kpis) {
  const reportScores = extractSectionScores(report);
  const derived = orgSectionScoresFromKpis(kpis);
  const reportOverall = n(reportScores.overall);
  const stale =
    (reportOverall == null || reportOverall === 0) && derived.overall != null;
  if (!stale) return reportScores;
  return {
    base: derived.base ?? reportScores.base ?? 0,
    environment: derived.environment ?? reportScores.environment ?? 0,
    social: derived.social ?? reportScores.social ?? 0,
    governance: derived.governance ?? reportScores.governance ?? 0,
    overall: derived.overall,
  };
}

export function buildSectionComparison(report, benchmarkType, options = {}) {
  const orgScores = options.orgScores || extractSectionScores(report);
  const priorReport = report?.last6YearsReports?.[0];
  const reference = referenceForType(benchmarkType, {
    schemas: options.schemas || [],
    priorScores: priorReport ? extractSectionScores(priorReport) : null,
    customTargets: options.customSectionTargets,
    priorByCode: options.priorByCode,
  }) || {};

  return SECTION_META.map((section) => {
    const org = orgScores[section.key] ?? 0;
    const benchmark = reference[section.key] ?? null;
    const gap = benchmark == null ? null : org - benchmark;
    return {
      ...section,
      orgScore: org,
      benchmarkScore: benchmark,
      gap,
      gapPercent: benchmark ? (gap / benchmark) * 100 : null,
      status: gap == null ? "no_data" : gap >= 2 ? "ahead" : gap <= -2 ? "behind" : "on_par",
    };
  });
}

export function buildBenchmarkPayload({
  report,
  schemas = [],
  submissions,
  benchmarkType,
  customSectionTargets,
  customKpiTargets,
}) {
  const orgContext = extractOrgContext(report);
  const priorReport = report?.last6YearsReports?.[0];
  const priorSubmissions = optionsPriorSubmissions(priorReport);
  const priorByCode = Object.fromEntries(
    priorSubmissions
      .filter((s) => s.calculated?.kpiValue != null)
      .map((s) => [s.schema.code, s.calculated.kpiValue]),
  );

  const kpis = buildKpiComparisons({
    schemas,
    submissions,
    benchmarkType,
    priorSubmissions,
    customKpiTargets,
  });
  const orgScores = resolveOrgScores(report, kpis);
  const sections = buildSectionComparison(report, benchmarkType, {
    schemas,
    customSectionTargets,
    priorByCode,
    orgScores,
  });

  const reference = referenceForType(benchmarkType, {
    schemas,
    priorScores: priorReport ? extractSectionScores(priorReport) : null,
    customTargets: customSectionTargets,
    priorByCode,
  }) || {};
  const benchmarkScore = n(reference.overall);
  const overall = {
    orgScore: orgScores.overall,
    benchmarkScore,
    gap: benchmarkScore == null ? null : orgScores.overall - benchmarkScore,
  };

  return {
    benchmarkType,
    benchmarkLabel:
      BENCHMARK_TYPES.find((b) => b.id === benchmarkType)?.label || benchmarkType,
    orgContext,
    overall,
    sections,
    kpis,
    priorReportYear: priorReport?.year || null,
    dashboard: buildBenchmarkDashboard(report, { overall, orgContext }),
  };
}

function optionsPriorSubmissions(priorReport) {
  if (!priorReport?.dynamicKPIs) return [];
  return Object.entries(priorReport.dynamicKPIs).map(([code, entry]) => ({
    schema: { code },
    calculated: entry.calculated,
    values: entry.values,
    createdAt: new Date(),
  }));
}

export function generateGapInsights(payload) {
  const insights = [];
  const { sections, kpis, benchmarkLabel, overall, priorReportYear } = payload;

  if (overall.gap != null && overall.gap >= 5) {
    insights.push({
      type: "positive",
      title: "Overall performance ahead of benchmark",
      text: `Your overall SI score is ${overall.orgScore?.toFixed(1)} — ${overall.gap.toFixed(1)} points above ${benchmarkLabel}.`,
    });
  } else if (overall.gap != null && overall.gap <= -5) {
    insights.push({
      type: "priority",
      title: "Overall gap to close",
      text: `Your score trails ${benchmarkLabel} by ${Math.abs(overall.gap).toFixed(1)} points. Focus on the largest KPI gaps below.`,
    });
  }

  const weakest = [...sections].filter((s) => s.gap != null).sort((a, b) => a.gap - b.gap)[0];
  if (weakest?.gap < -3) {
    insights.push({
      type: "focus",
      title: `${weakest.label} needs attention`,
      text: `This pillar scores ${weakest.orgScore?.toFixed(1)} vs benchmark ${weakest.benchmarkScore?.toFixed(1)} (${weakest.gap.toFixed(1)} pt gap).`,
    });
  }

  kpis.filter((k) => k.status === "behind").slice(0, 3).forEach((kpi) => {
    insights.push({
      type: "action",
      title: `Improve ${kpi.name}`,
      text: `Current: ${formatVal(kpi.orgValue)} ${kpi.unit} | Benchmark: ${formatVal(kpi.benchmarkValue)} ${kpi.unit}. ${
        kpi.lowerIsBetter ? "Reduce" : "Increase"
      } it to close the gap.`,
      code: kpi.code,
    });
  });

  kpis.filter((k) => k.status === "ahead").slice(0, 2).forEach((kpi) => {
    insights.push({
      type: "positive",
      title: `Strength: ${kpi.name}`,
      text: `You outperform ${benchmarkLabel} on this KPI. Maintain practices and document for audit.`,
      code: kpi.code,
    });
  });

  if (payload.benchmarkType === "previous_assessment" && priorReportYear && overall.gap != null) {
    insights.unshift({
      type: overall.gap >= 0 ? "positive" : "focus",
      title: `Year-over-year vs ${priorReportYear}`,
      text:
        overall.gap >= 0
          ? `Overall score improved by ${overall.gap.toFixed(1)} points since your prior assessment.`
          : `Overall score declined by ${Math.abs(overall.gap).toFixed(1)} points since ${priorReportYear}. Review changed KPIs.`,
    });
  }

  return insights.slice(0, 8);
}

/** Headline metrics for the benchmark dashboard (scores, grade, year). */
export function buildBenchmarkDashboard(report = {}, analysis = {}) {
  const orgScore = n(analysis?.overall?.orgScore) ?? n(report?.score) ?? 0;
  const benchmarkScore = n(analysis?.overall?.benchmarkScore);
  const orgContext = analysis?.orgContext || extractOrgContext(report);
  return {
    overallSustainabilityScore: orgScore,
    overallBenchmarkScore: benchmarkScore,
    scoreGap: benchmarkScore == null ? null : orgScore - benchmarkScore,
    esgRatingGrade: String(report?.grade || "").trim() || gradeFromScore(orgScore),
    benchmarkGrade: benchmarkScore == null ? null : gradeFromScore(benchmarkScore),
    assessmentYear: report?.year || new Date().getFullYear(),
    sector: orgContext.sector,
    country: orgContext.country,
  };
}

function formatVal(v) {
  if (v == null) return "—";
  if (Math.abs(v) >= 1000) return v.toLocaleString(undefined, { maximumFractionDigits: 1 });
  return Number(v).toFixed(2);
}
